import { Component, Input, OnInit } from '@angular/core';
import { CERTIFICATS_STATS_MOCK, CertificatStat, GenreCount } from '../../models/stat-connexion.model';
import { exporterStatsConnexionVersExcel, exporterTableauConnexionVersExcel } from '../../models/stat.util';
import { RapportService } from '../../services/rapport.service';
import { ApiCertificat, ApiCertificatReponse, ApprenantJamaisConnecte, mapReponseApiVersCertificatStats, calculerTotauxDedupliquesDepuisApi } from '../../models/stat-connexion-api.mapper';

interface CarteResume {
  label: string;
  valeur: number;
  sousTexte: string;
  icone: 'effectif' | 'effectif-fode' | 'connectes' | 'jamais-connectes';
}

interface LignePourcentage {
  nom: string;
  effectif: GenreCount;
  connectes: GenreCount;
  jamaisConnectes: GenreCount;
}

@Component({
  selector: 'app-statistiquesrapport',
  templateUrl: './statistiquesrapport.component.html',
  styleUrls: ['./statistiquesrapport.component.css']
})
export class StatistiquesrapportComponent implements OnInit {
  @Input() cohorte: any;
  @Input() type: any;
  periodeLabel: any;

  certificats: CertificatStat[] = CERTIFICATS_STATS_MOCK;
  totaux!: CertificatStat;

  totalEnrolledFode = 0;
  pourcentages: LignePourcentage[] = [];
  totalPourcentages!: LignePourcentage;

  cartesResume: CarteResume[] = [];

  chargementEnCours = true;
  erreurChargement = false;
  reponseBruteApi?: ApiCertificatReponse;
  envoiNotificationEnCours = false;
  popupNotification: { visible: boolean; succes: boolean; message: string } = {
    visible: false,
    succes: false,
    message: '',
  };
  popupConfirmationNotification = false;
  certificatSelectionne: ApiCertificat | null = null;
  chargementDetailEnCours = false;
  erreurDetailCertificat = false;
  detailVisible = false;

  constructor(private appelService: RapportService) { }

  ngOnInit(): void {
    const aujourdHui = new Date();
    const dateFormatee = aujourdHui.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');

    this.periodeLabel = `du ${dateFormatee}`;
    this.chargerStats();
  }

  chargerStats(): void {
    this.chargementEnCours = true;
    this.erreurChargement = false;

    this.appelService.getStatConnectionbyCohortandType(this.cohorte, this.type).subscribe({
      next: (reponse: ApiCertificatReponse) => {
        this.reponseBruteApi = reponse;
        this.totalEnrolledFode = reponse?.totalEnrolledFode ?? 0;

        const resultats = mapReponseApiVersCertificatStats(reponse);
        this.certificats = this.trierParEffectif(resultats);

        this.recalculerAgregats();

        this.chargementEnCours = false;
      },
      error: (err) => {
        // console.error('Erreur lors du chargement des statistiques de connexion', err);
        this.erreurChargement = true;
        this.chargementEnCours = false;
      },
    });
  }
  private trierParEffectif(certificats: CertificatStat[]): CertificatStat[] {
    return [...certificats].sort((a, b) => (b.effectif?.total || 0) - (a.effectif?.total || 0));
  }

  private recalculerAgregats(): void {
    this.totaux = calculerTotauxDedupliquesDepuisApi(this.reponseBruteApi);

    const pourcentagesCalcules = this.certificats.map((c) => this.calculerLignePourcentage(c, this.totaux));
    this.pourcentages = pourcentagesCalcules.sort((a, b) => (b.effectif?.total || 0) - (a.effectif?.total || 0));

    this.totalPourcentages = this.calculerLignePourcentage(this.totaux, this.totaux);
    this.cartesResume = this.construireCartesResume(this.totaux);
  }
  ouvrirDetailCertificat(certificat: CertificatStat): void {
    this.detailVisible = true;
    this.chargementDetailEnCours = true;
    this.erreurDetailCertificat = false;
    this.certificatSelectionne = null;

    this.appelService.getSingleCertificateTracking(certificat.id, this.type).subscribe({
      next: (reponse) => {
        this.certificatSelectionne = reponse;
        this.chargementDetailEnCours = false;
      },
      error: (err) => {
        // console.error('Erreur lors du chargement du détail du certificat', err);
        this.erreurDetailCertificat = true;
        this.chargementDetailEnCours = false;
      },
    });
  }

  fermerDetailCertificat(): void {
    this.detailVisible = false;
    this.certificatSelectionne = null;
    this.certificatIdSelectionne = '';
  }
  certificatIdSelectionne: string = '';

  onSelectionCertificat(id: string): void {
    const certificat = this.certificats.find((c) => c.id === id);
    if (certificat) {
      this.ouvrirDetailCertificat(certificat);
    }
  }
  private readonly NB_DECIMALES_POURCENTAGE = 2;

  private pourcentage(valeur: number, base: number): number {
    if (base <= 0) {
      return 0;
    }
    const brut = (valeur / base) * 100;
    const [entier, decimales = ''] = brut.toFixed(10).split('.');
    const decimalesTronquees = decimales.padEnd(this.NB_DECIMALES_POURCENTAGE, '0').slice(0, this.NB_DECIMALES_POURCENTAGE);
    return parseFloat(`${entier}.${decimalesTronquees}`);
  }

  formaterPourcentage(valeur: number): string {
    return valeur.toFixed(this.NB_DECIMALES_POURCENTAGE);
  }

  private calculerLignePourcentage(c: CertificatStat, totaux: CertificatStat): LignePourcentage {
    return {
      nom: c.nom,
      effectif: {
        hommes: this.pourcentage(c.effectif.hommes, totaux.effectif.total),
        femmes: this.pourcentage(c.effectif.femmes, totaux.effectif.total),
        nonRenseigne: this.pourcentage(c.effectif.nonRenseigne, totaux.effectif.total),
        total: this.pourcentage(c.effectif.total, totaux.effectif.total),
      },
      connectes: {
        hommes: this.pourcentage(c.connectes.hommes, totaux.connectes.total),
        femmes: this.pourcentage(c.connectes.femmes, totaux.connectes.total),
        nonRenseigne: this.pourcentage(c.connectes.nonRenseigne, totaux.connectes.total),
        total: this.pourcentage(c.connectes.total, totaux.connectes.total),
      },
      jamaisConnectes: {
        hommes: this.pourcentage(c.jamaisConnectes.hommes, totaux.jamaisConnectes.total),
        femmes: this.pourcentage(c.jamaisConnectes.femmes, totaux.jamaisConnectes.total),
        nonRenseigne: this.pourcentage(c.jamaisConnectes.nonRenseigne, totaux.jamaisConnectes.total),
        total: this.pourcentage(c.jamaisConnectes.total, totaux.jamaisConnectes.total),
      },
    };
  }
  private construireCartesResume(totaux: CertificatStat): CarteResume[] {
    return [
      {
        label: 'Enrôlés FODE',
        valeur: this.totalEnrolledFode,
        sousTexte: 'Enrôlés sur FODE',
        icone: 'effectif-fode',
      },
      {
        label: 'Enrôlés Moodle',
        valeur: totaux.effectif.total,
        sousTexte: 'Effectif global Moodle',
        icone: 'effectif',
      },
      {
        label: 'Connectés',
        valeur: totaux.connectes.total,
        sousTexte: `${this.formaterPourcentage(this.pourcentage(totaux.connectes.total, totaux.effectif.total))}% de l'effectif`,
        icone: 'connectes',
      },
      {
        label: 'Jamais connectés',
        valeur: totaux.jamaisConnectes.total,
        sousTexte: `${this.formaterPourcentage(this.pourcentage(totaux.jamaisConnectes.total, totaux.effectif.total))}% de l'effectif`,
        icone: 'jamais-connectes',
      },
    ];
  }

  demanderConfirmationNotification(): void {
    const apprenants = this.extraireApprenantsJamaisConnectes();

    if (!apprenants.length) {
      this.afficherPopupNotification(false, 'Aucun apprenant jamais connecté à notifier.');
      return;
    }

    this.popupConfirmationNotification = true;
  }

  annulerConfirmationNotification(): void {
    this.popupConfirmationNotification = false;
  }

  confirmerEnvoiNotification(): void {
    this.popupConfirmationNotification = false;
    this.notifierJamaisConnectes();
  }

  private notifierJamaisConnectes(): void {
    const apprenants = this.extraireApprenantsJamaisConnectes();

    if (!apprenants.length) {
      this.afficherPopupNotification(false, 'Aucun apprenant jamais connecté à notifier.');
      return;
    }

    this.envoiNotificationEnCours = true;

    this.appelService.notifierApprenants(apprenants).subscribe({
      next: () => {
        this.envoiNotificationEnCours = false;
        this.afficherPopupNotification(
          true,
          `Notification envoyée avec succès à ${apprenants.length} apprenant(s).`
        );
      },
      error: (err) => {
        // console.error('Erreur lors de l\'envoi des notifications', err);
        this.envoiNotificationEnCours = false;
        this.afficherPopupNotification(
          false,
          'Une erreur est survenue lors de l\'envoi des notifications. Veuillez réessayer.'
        );
      },
    });
  }

  private afficherPopupNotification(succes: boolean, message: string): void {
    this.popupNotification = { visible: true, succes, message };
  }

  fermerPopupNotification(): void {
    this.popupNotification = { ...this.popupNotification, visible: false };
  }
  private extraireApprenantsJamaisConnectes(): ApprenantJamaisConnecte[] {
    if (!this.reponseBruteApi?.certificates) {
      return [];
    }

    const apprenants: ApprenantJamaisConnecte[] = [];

    for (const certif of this.reponseBruteApi.certificates) {
      if (!Array.isArray(certif.learners)) continue;

      for (const learner of certif.learners) {
        if (learner.neverConnected && learner.email) {
          apprenants.push({
            firstname: learner.firstname ?? '',
            lastname: learner.lastname ?? '',
            certificat: certif.certificateName ?? '',
            email: learner.email.trim().toLowerCase(),
          });
        }
      }
    }

    return apprenants;
  }

  async exporterXls(tableau: 'absolues' | 'pourcentages'): Promise<void> {
    await exporterTableauConnexionVersExcel(
      tableau,
      this.certificats,
      this.totaux,
      this.pourcentages,
      this.totalPourcentages,
      this.periodeLabel
    );
  }

  async exporterToutXls(): Promise<void> {
    await exporterStatsConnexionVersExcel(
      this.certificats,
      this.totaux,
      this.pourcentages,
      this.totalPourcentages,
      this.periodeLabel
    );
  }
}